import { useCallback, useMemo } from 'react';
import { FilterChangedEvent, FilterModel } from 'ag-grid-community';
import {
    useGetGridFilters,
    useMutateGridFilters,
} from 'routes/BudgetModule/components/shared/GridFilters/hooks';
import * as Sentry from '@sentry/browser';
import { BUDGET_YEAR_FIELD } from 'routes/BudgetModule/config/constants';
type UseGridFiltersHelper = {
    updateAnalyseGridFilters: (event: FilterChangedEvent) => void;
    updateProjectionGridFilters: (event: FilterChangedEvent) => void;
    gridFilters: FilterModel | undefined;
};

/**
 * This is mainly a shot in the dark because of a reported bug where Analyze would
 * load without any year displayed. I've put it in the frontend to be as close as AgGrid as possible.
 * If there isn't any issue in Sentry after a few months (say after september 2025), we can remove this.
 */
export const validateGridFilters = (gridFilters: FilterModel) => {
    const { analyseGridFilters } = gridFilters;
    if (!analyseGridFilters) return { isValid: true };
    if (BUDGET_YEAR_FIELD in analyseGridFilters) {
        const { values, filterType } = analyseGridFilters[BUDGET_YEAR_FIELD];
        if (!values || !filterType)
            return {
                isValid: false,
                errorMsg: `Missing values or filterType in ${JSON.stringify(analyseGridFilters)}`,
            };
        const areCorrectYears = values.every((year: string) =>
            Boolean(year.match(/^\d{4}$/)),
        );
        if (values.length === 0) {
            return {
                isValid: false,
                errorMsg: `There is no entries in 'values' which is not a legit use case, so we ignore the filter to avoid confusion for the user: ${JSON.stringify(analyseGridFilters)}`,
            };
        }
        const isCorrectFilterType = filterType === 'set';
        if (!areCorrectYears) {
            return {
                isValid: false,
                errorMsg: `Invalid year format in ${JSON.stringify(analyseGridFilters)}`,
            };
        }
        if (!isCorrectFilterType) {
            return {
                isValid: false,
                errorMsg: `Invalid filterType in ${JSON.stringify(analyseGridFilters)}`,
            };
        }
        return { isValid: true };
    }
    return { isValid: true };
};

class InvalidGridFiltersError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'InvalidGridFiltersError';
    }
}
/**
 * Custom hook to help managing grid filters (within right panel).
 * `updateAnalyseGridFilters` and `updateProjectionGridFilters`: Functions to update the user's grid filter settings based on filter changes.
 * `gridFilters`: The current filters as fetched from backend, determined by the exercice
 */
export const useGridFiltersHelper = (
    exercice: number,
): UseGridFiltersHelper => {
    const { mutate: mutateGridFilters } = useMutateGridFilters(exercice);
    const { gridFilters: rawGridFilters } = useGetGridFilters(exercice);
    const updateOneGridFilters = useCallback(
        (
            event: FilterChangedEvent,
            key: 'analyseGridFilters' | 'projectionsGridFilters',
        ) => {
            mutateGridFilters({
                exercice,
                [key]: event.api.getFilterModel(),
            });
        },
        [mutateGridFilters, exercice],
    );

    const updateAnalyseGridFilters = (event: FilterChangedEvent) =>
        updateOneGridFilters(event, 'analyseGridFilters');
    const updateProjectionGridFilters = (event: FilterChangedEvent) =>
        updateOneGridFilters(event, 'projectionsGridFilters');

    const gridFilters = useMemo(() => {
        return rawGridFilters;
    }, [JSON.stringify(rawGridFilters)]);

    const { isValid, errorMsg } = validateGridFilters(rawGridFilters);
    if (!isValid) {
        const msg = `Grid filters are not valid, please check the filter model. Details: ${errorMsg}`;
        console.error(msg);
        Sentry.captureException(new InvalidGridFiltersError(msg));
    }
    return {
        gridFilters: isValid ? gridFilters : undefined,
        updateAnalyseGridFilters,
        updateProjectionGridFilters,
    };
};
